import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    q: "When does Coly launch?",
    a: "We're aiming for a public launch in summer 2026. If you'd like to test Coly before then, drop us a line at support@colyapp.com — we'll get you onboarded.",
  },
  {
    q: "Is my health data safe?",
    a: "Always. Your data is encrypted, sensitive notes and photos are PIN-protected on your device, and we never sell or share anything. Your story belongs to you.",
  },
  {
    q: "Will Coly replace my doctor?",
    a: "Nope, and it never will. Coly is a friendly companion that helps you notice patterns and have richer conversations with your real care team.",
  },
  {
    q: "Is it free?",
    a: "Yes — there will always be a free version of Coly with the core symptom-tracking essentials. Later on, we'll add a premium plan with extra features (think advanced AI insights, deeper reports, etc.) for those who want more. Join the waitlist to get early access to both.",
  },
  {
    q: "I don't have ulcerative colitis — can I still use it?",
    a: "Yes. Coly is built around UC and Crohn's, but anyone tracking gut symptoms, IBS, or post-op recovery will feel right at home.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="px-6 pt-16 pb-24">
      <div className="max-w-[880px] mx-auto">
        <div className="text-center mb-10">
          <div className="inline-block text-[11px] font-extrabold tracking-[1.6px] text-coly-green-deep uppercase mb-3.5">
            Questions
          </div>
          <h2 className="text-4xl sm:text-[42px] font-black tracking-tight leading-[1.05] m-0">
            The honest small print.
          </h2>
        </div>

        <div data-testid="faq-accordion">
          {faqs.map((item, idx) => {
            const isOpen = open === idx;
            return (
              <div
                key={item.q}
                data-testid={`faq-item-${idx}`}
                className="border-t border-coly-ink/10"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : idx)}
                  aria-expanded={isOpen}
                  className="w-full text-left py-5 cursor-pointer bg-transparent border-0 p-0"
                >
                  <div className="flex items-center justify-between gap-6">
                    <h4 className="text-[19px] font-extrabold tracking-tight m-0">{item.q}</h4>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-[22px] font-bold shrink-0 transition-colors ${
                        isOpen ? "bg-coly-green text-white" : "bg-coly-surface-alt text-coly-ink"
                      }`}
                    >
                      {isOpen ? "–" : "+"}
                    </div>
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <p className="text-[15px] text-coly-ink-soft leading-relaxed mt-1 mb-5 font-semibold max-w-[720px]">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
          <div className="border-t border-coly-ink/10" />
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center" data-testid="faq-contact">
          <p className="text-coly-ink-soft text-[15px] font-semibold mb-4">
            Still have a question we didn't answer?
          </p>
          <a
            href="mailto:support@colyapp.com"
            data-testid="contact-email-btn"
            className="inline-flex items-center gap-2 bg-coly-ink text-coly-cream px-6 py-3.5 rounded-full font-extrabold text-[15px] hover:bg-coly-green-deep transition-colors"
            style={{ boxShadow: "0 4px 0 rgba(47,58,44,0.18)" }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
            Email us — support@colyapp.com
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
